import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import StorageIcon from '@mui/icons-material/Storage';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getQuotaReport, getQuotaHistory, QuotaReport, QuotaHistory, ArticlePoolHealth, getWarmingHistory, WarmingRunGroup, getMoneyFlowHealth, MoneyFlowHealth, getSystemHealth, SystemHealth, getArticlePoolEnhancedStatus, apiRequest } from '../lib/api';

// Staleness threshold - must match backend HEALTH_THRESHOLDS.ARTICLE_STALENESS_HOURS
const THRESHOLD_HOURS = 24;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`resource-tabpanel-${index}`}
      aria-labelledby={`resource-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `resource-tab-${index}`,
    'aria-controls': `resource-tabpanel-${index}`,
  };
}

export default function Resources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  const [tabValue, setTabValue] = useState(() => {
    if (tabParam === 'quotas') return 0;
    if (tabParam === 'articlepool') return 1;
    if (tabParam === 'moneyflow') return 2;
    return 1; // Default to Article Pool
  });

  const [quotaReport, setQuotaReport] = useState<QuotaReport | null>(null);
  const [history, setHistory] = useState<QuotaHistory | null>(null);
  const [warmingHistory, setWarmingHistory] = useState<WarmingRunGroup[]>([]);
  const [moneyFlowHealth, setMoneyFlowHealth] = useState<any>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [enhancedStatus, setEnhancedStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warmingHistoryError, setWarmingHistoryError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [harvesting, setHarvesting] = useState(false);

  // Critical threshold (configurable)
  const CRITICAL_AGE_HOURS = 24;

  // Pool filter state with URL sync
  const poolFilterParam = searchParams.get('poolFilter');
  const validFilters = ['all', 'category', 'sector', 'stock', 'oldest', 'stale', 'expired', 'zero'];
  const [poolFilter, setPoolFilter] = useState<'all' | 'category' | 'sector' | 'stock' | 'oldest' | 'stale' | 'expired' | 'zero'>(() => {
    return validFilters.includes(poolFilterParam || '') ? (poolFilterParam as any) : 'all';
  });

  // Refs for scroll and focus management
  const poolDetailsRef = React.useRef<HTMLDivElement>(null);
  const poolTableRef = React.useRef<HTMLTableElement>(null);

  // Mobile view state
  const [mobileView, setMobileView] = useState<'overview' | 'detail'>('overview');

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [report, historyData] = await Promise.all([
        getQuotaReport(),
        getQuotaHistory(),
      ]);
      setQuotaReport(report);
      setHistory(historyData);

      // Fetch optional data (best-effort)
      const optionalFetches = [
        getWarmingHistory(10).catch(error => {
          console.warn('Failed to load warming history:', error);
          setWarmingHistoryError(error instanceof Error ? error.message : 'Failed to load warming history');
          return null as WarmingRunGroup[] | null;
        }),
        getMoneyFlowHealth().catch(error => {
          console.warn('Failed to load money flow health:', error);
          return null as MoneyFlowHealth | null;
        }),
        getSystemHealth().catch(error => {
          console.warn('Failed to load system health:', error);
          return null as SystemHealth | null;
        }),
        getArticlePoolEnhancedStatus().catch(error => {
          console.warn('Failed to load enhanced article pool status:', error);
          return null;
        }),
      ] as const;

      const [warmingData, moneyFlowData, systemHealthData, enhancedData] = await Promise.all(optionalFetches);

      if (warmingData) {
        setWarmingHistory(warmingData);
        setWarmingHistoryError(null);
      } else {
        setWarmingHistoryError(prev => prev || 'Failed to load warming history');
      }

      if (moneyFlowData) {
        setMoneyFlowHealth(moneyFlowData);
      }

      if (systemHealthData) {
        setSystemHealth(systemHealthData);
      }

      if (enhancedData) {
        setEnhancedStatus(enhancedData);
      }

      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load resource data');
    } finally {
      setLoading(false);
    }
  };

  const handleForceHarvest = async () => {
    if (!confirm('Are you sure you want to trigger a manual harvest? This will consume quota.')) return;
    
    setHarvesting(true);
    try {
      const data = await apiRequest<{ success: boolean; error?: string }>('/api/admin/article-pool/harvest', {
        method: 'POST',
      });

      if (data.success) {
        alert('Harvest triggered successfully!');
        await fetchData(); // Refresh data
      } else {
        alert(`Harvest failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Harvest failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setHarvesting(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const tabNames = ['quotas', 'articlepool', 'moneyflow'];
    setSearchParams({ tab: tabNames[newValue] });
  };

  const handleMobileCardClick = (tabIndex: number) => {
    setTabValue(tabIndex);
    setMobileView('detail');
    const tabNames = ['quotas', 'articlepool', 'moneyflow'];
    setSearchParams({ tab: tabNames[tabIndex] });
  };

  // Update pool filter with URL sync
  const updatePoolFilter = (filter: 'all' | 'stale' | 'expired' | 'zero') => {
    setPoolFilter(filter);
    const params = new URLSearchParams(window.location.search);
    params.set('poolFilter', filter);
    params.set('tab', 'articlepool');
    window.history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
  };

  // Scroll to pool details table with offset and focus
  const scrollToPoolTable = () => {
    const el = poolDetailsRef.current;
    if (!el) return;

    const headerOffset = 64; // Sticky header height
    const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });

    // Focus table after scroll completes (400ms)
    setTimeout(() => {
      poolTableRef.current?.focus();
    }, 400);
  };

  // View expired pools with deep link and scroll
  const viewExpiredPools = () => {
    updatePoolFilter('expired');
    scrollToPoolTable();

    // Telemetry
    console.log('[Resources] view_expired_pools_clicked', {
      timestamp: new Date().toISOString(),
      expiredCount: enhancedStatus?.summary?.expiredPools || 0,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon fontSize="small" />;
      case 'warning':
        return <WarningAmberIcon fontSize="small" />;
      case 'critical':
        return <ErrorIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  const getProgressColor = (percentUsed: number): 'success' | 'warning' | 'error' => {
    if (percentUsed >= 90) return 'error';
    if (percentUsed >= 70) return 'warning';
    return 'success';
  };

  if (loading && !quotaReport) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Resources & Health
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage external API quotas and monitor internal data pool health
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Last updated: {lastUpdated.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
          })} • Auto-refreshes every 5 minutes
        </Typography>
      </Box>

      {/* Mobile Overview Section */}
      <Box sx={{ display: { xs: mobileView === 'overview' ? 'block' : 'none', md: 'none' } }}>
        <Grid container spacing={2}>
          {/* System Status Card */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ fontSize: 40 }}>
                    {systemHealth?.status === 'healthy' ? '🟢' : systemHealth?.status === 'degraded' ? '🟡' : '🔴'}
                  </Box>
                  <Box>
                    <Typography variant="h6">System Status</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {systemHealth?.status === 'healthy'
                        ? 'All services operating normally'
                        : 'Issues detected - tap for details'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Article Pool Card */}
          <Grid size={{ xs: 12 }}>
            <Card onClick={() => handleMobileCardClick(1)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>Article Pool</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quotaReport?.articlePool?.metrics?.totalArticles ?? 0} articles
                    </Typography>
                    <Typography variant="caption" color="success.main">
                       {quotaReport?.articlePool?.metrics?.articlesLast24h ?? 0} fresh
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={quotaReport?.articlePool?.status?.toUpperCase() ?? 'UNKNOWN'}
                      color={getStatusColor(quotaReport?.articlePool?.status || 'unknown')}
                      size="small"
                    />
                    <PlayArrowIcon fontSize="small" color="action" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Money Flow Card */}
          <Grid size={{ xs: 12 }}>
            <Card onClick={() => handleMobileCardClick(2)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>Money Flow</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {systemHealth?.services?.moneyFlowPool?.completeness ?? 0}% complete
                    </Typography>
                    <Typography variant="caption" color={systemHealth?.services?.moneyFlowPool?.fresh ? 'success.main' : 'text.secondary'}>
                       {systemHealth?.services?.moneyFlowPool?.fresh ?? 0} fresh
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={systemHealth?.services?.moneyFlowPool?.status?.toUpperCase() ?? 'UNKNOWN'}
                      color={getStatusColor(systemHealth?.services?.moneyFlowPool?.status || 'unknown')}
                      size="small"
                    />
                    <PlayArrowIcon fontSize="small" color="action" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* API Quotas Card */}
          <Grid size={{ xs: 12 }}>
            <Card onClick={() => handleMobileCardClick(0)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>API Quotas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quotaReport?.summary?.totalServices ?? 0} services
                    </Typography>
                    {quotaReport?.summary?.servicesAtRisk ? (
                       <Typography variant="caption" color="warning.main">
                         {quotaReport.summary.servicesAtRisk} at risk
                       </Typography>
                    ) : (
                      <Typography variant="caption" color="success.main">
                        Stable
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={quotaReport?.summary?.overallStatus?.toUpperCase() ?? 'UNKNOWN'}
                      color={getStatusColor(quotaReport?.summary?.overallStatus || 'unknown')}
                      size="small"
                    />
                    <PlayArrowIcon fontSize="small" color="action" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Desktop / Detail View */}
      <Box sx={{ display: { xs: mobileView === 'detail' ? 'block' : 'none', md: 'block' } }}>
        {/* Mobile Back Button */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <Button 
            startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />} 
            onClick={() => setMobileView('overview')}
            variant="outlined"
            size="small"
          >
            Back to Overview
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="resource tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, md: 64 },
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              px: { xs: 2, md: 3 }
            }
          }}
        >
          <Tab icon={<CloudQueueIcon />} iconPosition="start" label="External API Quotas" {...a11yProps(0)} />
          <Tab icon={<StorageIcon />} iconPosition="start" label="Article Pool" {...a11yProps(1)} />
          <Tab icon={<StorageIcon />} iconPosition="start" label="Money Flow Pool" {...a11yProps(2)} />
        </Tabs>
      </Box>

      {/* Tab 1: External API Quotas */}
      <CustomTabPanel value={tabValue} index={0}>
        {/* Summary Cards */}
        {quotaReport && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Services
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {quotaReport.summary.totalServices}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    At Risk
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="warning.main">
                    {quotaReport.summary.servicesAtRisk}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" gutterBottom>
                      Overall Status
                    </Typography>
                    <Chip
                      label={quotaReport.summary.overallStatus.toUpperCase()}
                      color={getStatusColor(quotaReport.summary.overallStatus)}
                      size="small"
                      icon={getStatusIcon(quotaReport.summary.overallStatus)}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Service Quota Details */}
        {quotaReport && quotaReport.quotas.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              API Quota Status
            </Typography>
            <Grid container spacing={3}>
              {quotaReport.quotas.map((quota) => (
                <Grid size={{ xs: 12 }} key={quota.service}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {quota.service}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {quota.used} / {quota.limit} {(quota as any).quotaType || ''} requests used
                            {(quota as any).resetIn && ` • Resets in ${(quota as any).resetIn}`}
                          </Typography>
                        </Box>
                        <Chip
                          label={quota.status.toUpperCase()}
                          color={getStatusColor(quota.status)}
                          icon={getStatusIcon(quota.status)}
                        />
                      </Box>

                      <Box mb={1}>
                        <LinearProgress
                          variant="determinate"
                          value={quota.percentUsed}
                          color={getProgressColor(quota.percentUsed)}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                      </Box>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Used
                          </Typography>
                          <Typography variant="h6">{quota.used}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Remaining
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {quota.remaining}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Percentage
                          </Typography>
                          <Typography variant="h6">{quota.percentUsed.toFixed(1)}%</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">
                            Resets In
                          </Typography>
                          <Typography variant="h6" color="info.main">
                            {(quota as any).resetIn || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* NewsData.io quota limits info */}
                      {(quota as any).hourlyLimit && (
                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Hourly</Typography>
                              <Typography variant="body2">{quota.used} / {(quota as any).hourlyLimit}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">Daily</Typography>
                              <Typography variant="body2">{(quota as any).dailyUsed ?? 0} / {(quota as any).dailyLimit}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* Finnhub allocations (if available) */}
                      {quota.allocations && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Daily Budget Allocations
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 4 }}>
                              <Typography variant="caption" color="text.secondary">
                                Scheduled Harvest
                              </Typography>
                              <Typography variant="body2">{quota.allocations.scheduled || 0} remaining</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                              <Typography variant="caption" color="text.secondary">
                                Emergency Fetch
                              </Typography>
                              <Typography variant="body2">{quota.allocations.emergency || 0} remaining</Typography>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                              <Typography variant="caption" color="text.secondary">
                                On-Demand
                              </Typography>
                              <Typography variant="body2">{quota.allocations.onDemand || 0} remaining</Typography>
                            </Grid>
                          </Grid>
                          {quota.metrics?.emergencyUsed && quota.metrics.emergencyUsed > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="body2">
                                Emergency fetches used today: {quota.metrics.emergencyUsed}
                                {quota.metrics.lastEmergencyCall && (
                                  <> (Last: {new Date(quota.metrics.lastEmergencyCall).toLocaleString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    timeZoneName: 'short'
                                  })})</>
                                )}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Historical Data */}
        {history && history.yesterday && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Historical Usage
            </Typography>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Yesterday ({new Date(history.yesterday.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    timeZoneName: 'short'
                  })})
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Service
                    </Typography>
                    <Typography variant="body1">{history.yesterday.service}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Used
                    </Typography>
                    <Typography variant="body1">{history.yesterday.used}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Daily Limit
                    </Typography>
                    <Typography variant="body1">{history.yesterday.limit}</Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  {history.note}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </CustomTabPanel>

      {/* Tab 2: Data Pools */}
      <CustomTabPanel value={tabValue} index={1}>
        {/* Section 1: Article Pool Health - What operators care about most */}
        {quotaReport?.articlePool && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Article Pool Health
            </Typography>

            {/* Section 1: Article Pool Status - What operators care about most */}
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                  <Typography variant="h6">Article Pool Status</Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={harvesting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                      disabled={harvesting}
                      onClick={handleForceHarvest}
                    >
                      {harvesting ? 'Harvesting...' : 'Force Harvest'}
                    </Button>
                    <Chip
                      label={quotaReport.articlePool.status.toUpperCase()}
                      color={getStatusColor(quotaReport.articlePool.status)}
                      icon={getStatusIcon(quotaReport.articlePool.status)}
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {quotaReport.articlePool.message}
                </Typography>

                {quotaReport.articlePool.status === 'critical' && (() => {
                  // Backend-derived count (source of truth - includes expired + not_found)
                  const expiredCount = (enhancedStatus?.summary?.expiredPools || 0) + (enhancedStatus?.summary?.notFoundPools || 0);
                  const hasExpiredPools = expiredCount > 0;

                  return (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        Why is this CRITICAL?
                        {hasExpiredPools && (
                          <Chip
                            label={`${expiredCount} pools expired`}
                            color="error"
                            size="small"
                            sx={{ ml: 1 }}
                            title={`Includes ${enhancedStatus?.summary?.notFoundPools || 0} not_found pools`}
                          />
                        )}
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, my: 0 }}>
                        <li>
                          Articles older than {THRESHOLD_HOURS}h are considered stale; current oldest:{' '}
                          {typeof quotaReport.articlePool.metrics?.oldestArticleAge === 'number'
                            ? `${quotaReport.articlePool.metrics.oldestArticleAge.toFixed(1)}h`
                            : 'unknown'}
                        </li>
                        <li>Stale articles reduce sentiment analysis accuracy and market relevance</li>
                        <li>
                          Last harvest:{' '}
                          {quotaReport.articlePool.metrics?.lastHarvestTime
                            ? `${Math.max(0, (Date.now() - new Date(quotaReport.articlePool.metrics.lastHarvestTime).getTime()) / 3600000).toFixed(1)}h ago`
                            : 'unknown'}
                        </li>
                      </Box>
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2">
                          <strong>Action Required:</strong> Trigger manual harvest or check NewsData.io quota limits
                        </Typography>
                        {hasExpiredPools && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={viewExpiredPools}
                          >
                            View Expired Pools
                          </Button>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Last computed: {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </Typography>
                    </Alert>
                  );
                })()}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Articles
                    </Typography>
                    <Typography variant="h6">{quotaReport.articlePool.metrics.totalArticles}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Symbols Covered
                    </Typography>
                    <Typography variant="h6">{quotaReport.articlePool.metrics.symbolsCovered}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Fresh (&lt;24h)
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {quotaReport.articlePool.metrics.articlesLast24h || 0}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Stale (&gt;24h)
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {quotaReport.articlePool.metrics.articlesOlderThan24h || 0}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Oldest Article
                    </Typography>
                    <Typography variant="h6">
                      {quotaReport.articlePool.metrics.oldestArticleAge.toFixed(1)}h
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Average Age
                    </Typography>
                    <Typography variant="h6">
                      {quotaReport.articlePool.metrics.averageArticleAge.toFixed(1)}h
                    </Typography>
                  </Grid>
                  {quotaReport.articlePool.metrics.hitRate !== undefined && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Hit Rate
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {quotaReport.articlePool.metrics.hitRate.toFixed(1)}%
                      </Typography>
                    </Grid>
                  )}
                  {quotaReport.articlePool.metrics.lastHarvestTime && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Last Harvest
                      </Typography>
                      <Typography variant="body2">
                        {new Date(quotaReport.articlePool.metrics.lastHarvestTime).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZoneName: 'short'
                        })}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Stalest/Freshest Pools Strip */}
                {enhancedStatus?.pools && (() => {
                  const pools = Object.entries(enhancedStatus.pools)
                    .map(([key, pool]: [string, any]) => ({
                      key,
                      age: pool.ageHours,
                      articles: pool.counts?.articles ?? 0
                    }))
                    .filter(p => typeof p.age === 'number');

                  const stalest = [...pools].sort((a, b) => b.age - a.age).slice(0, 5);
                  const freshest = [...pools].sort((a, b) => a.age - b.age).slice(0, 5);

                  return (
                    <Box sx={{ mt: 3 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom color="error.main">
                                🔥 Stalest Pools
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, my: 0 }}>
                                {stalest.map(p => (
                                  <li key={p.key}>
                                    <Typography variant="body2">
                                      <strong>{p.key}</strong>: {p.age.toFixed(1)}h ({p.articles} articles)
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom color="success.main">
                                ✨ Freshest Pools
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, my: 0 }}>
                                {freshest.map(p => (
                                  <li key={p.key}>
                                    <Typography variant="body2">
                                      <strong>{p.key}</strong>: {p.age.toFixed(1)}h ({p.articles} articles)
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })()}

                {/* Detailed Breakdown Accordion */}
                {quotaReport.articlePool.metrics.breakdown && (
                  <Box sx={{ mt: 3 }}>
                    <Accordion variant="outlined">
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Article Breakdown Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={3}>
                          {/* Categories Table */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" gutterBottom>By Category</Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(quotaReport.articlePool.metrics.breakdown.byCategory || {}).map(([name, count]) => (
                                    <TableRow key={name}>
                                      <TableCell component="th" scope="row">{name}</TableCell>
                                      <TableCell align="right">{count}</TableCell>
                                    </TableRow>
                                  ))}
                                  {Object.keys(quotaReport.articlePool.metrics.breakdown.byCategory || {}).length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={2} align="center">No category data</TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            </Box>
                          </Grid>
                          
                          {/* Symbols Table */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" gutterBottom>By Symbol</Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Symbol</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(quotaReport.articlePool.metrics.breakdown.bySymbol || {})
                                    .sort(([, a], [, b]) => b - a) // Sort by count desc
                                    .map(([name, count]) => (
                                    <TableRow key={name}>
                                      <TableCell component="th" scope="row">{name}</TableCell>
                                      <TableCell align="right">{count}</TableCell>
                                    </TableRow>
                                  ))}
                                  {Object.keys(quotaReport.articlePool.metrics.breakdown.bySymbol || {}).length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={2} align="center">No symbol data</TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            </Box>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Section 2: Harvest Execution Details */}
        {enhancedStatus && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Harvest Execution
            </Typography>
            <Card>
              <CardContent>
                <Grid container spacing={3}>
                  {/* Quota Status */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>NewsData.io Quota</Typography>
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography variant="h4">{enhancedStatus.quota?.remaining ?? '?'}</Typography>
                      <Typography variant="body2" color="text.secondary">/ {enhancedStatus.quota?.limit ?? '?'} remaining</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={enhancedStatus.quota?.utilizationPercent ?? 0}
                      color={enhancedStatus.quota?.status === 'healthy' ? 'success' : enhancedStatus.quota?.status === 'warning' ? 'warning' : 'error'}
                      sx={{ mt: 1, height: 8, borderRadius: 1 }}
                    />
                    {enhancedStatus.quota?.resetHumanReadable && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Resets in {enhancedStatus.quota.resetHumanReadable}
                      </Typography>
                    )}
                  </Grid>

                  {/* Provider Health */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Provider Health</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={enhancedStatus.providerHealth?.status?.toUpperCase() || 'UNKNOWN'}
                        color={
                          enhancedStatus.providerHealth?.status === 'healthy' ? 'success' :
                          enhancedStatus.providerHealth?.status === 'warning' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                      {enhancedStatus.providerHealth?.consecutiveFailures > 0 && (
                        <Typography variant="caption" color="error">
                          {enhancedStatus.providerHealth.consecutiveFailures} consecutive failures
                        </Typography>
                      )}
                    </Box>
                    {enhancedStatus.canaryMetrics?.last24Hours && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        24h uptime: {enhancedStatus.canaryMetrics.last24Hours.uptime?.toFixed(1) ?? 0}%
                      </Typography>
                    )}
                  </Grid>

                  {/* Skip Metrics */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Skip Reasons</Typography>
                    {enhancedStatus.skipMetrics?.totalSkips > 0 ? (
                      <Box>
                        <Typography variant="body2">
                          Freshness gate: {enhancedStatus.skipMetrics.freshness_gate?.count ?? 0}
                        </Typography>
                        <Typography variant="body2">
                          Quota exhausted: {enhancedStatus.skipMetrics.quota_exhausted?.count ?? 0}
                        </Typography>
                        <Typography variant="body2">
                          Budget limit: {enhancedStatus.skipMetrics.budget_limit?.count ?? 0}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No skips recorded</Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Recommendation */}
                {enhancedStatus.summary?.recommendation && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {enhancedStatus.summary.recommendation}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Section 3: Pool Details by Symbol */}
        {enhancedStatus?.pools && Object.keys(enhancedStatus.pools).length > 0 && (
          <Box ref={poolDetailsRef} id="pool-details-section" sx={{ mb: 4 }}>
            {/* Age Distribution Summary */}
            <Typography variant="h5" gutterBottom>Pool Overview</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(() => {
                const pools = Object.entries(enhancedStatus.pools);
                const categories = pools.filter(([k]) => k.startsWith('category:'));
                const sectors = pools.filter(([k]) => k.startsWith('sector:'));
                const stocks = pools.filter(([k]) => k.startsWith('stock:'));
                
                // Age buckets
                const ageBuckets = { '<6h': 0, '6-12h': 0, '12-24h': 0, '>24h': 0, 'unknown': 0 };
                pools.forEach(([_, p]: [string, any]) => {
                  const age = p.ageHours;
                  if (typeof age !== 'number') {
                    ageBuckets['unknown']++;
                  } else if (age < 6) {
                    ageBuckets['<6h']++;
                  } else if (age < 12) {
                    ageBuckets['6-12h']++;
                  } else if (age < 24) {
                    ageBuckets['12-24h']++;
                  } else {
                    ageBuckets['>24h']++;
                  }
                });

                return (
                  <>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Categories</Typography>
                          <Typography variant="h5">{categories.length}</Typography>
                          <Typography variant="caption" color="success.main">
                            {categories.filter(([_, p]: [string, any]) => p.freshnessStatus === 'fresh').length} fresh
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Sectors</Typography>
                          <Typography variant="h5">{sectors.length}</Typography>
                          <Typography variant="caption" color="success.main">
                            {sectors.filter(([_, p]: [string, any]) => p.freshnessStatus === 'fresh').length} fresh
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Stocks</Typography>
                          <Typography variant="h5">{stocks.length}</Typography>
                          <Typography variant="caption" color="success.main">
                            {stocks.filter(([_, p]: [string, any]) => p.freshnessStatus === 'fresh').length} fresh
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" color="text.secondary">Age Distribution</Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                            <Chip label={`<6h: ${ageBuckets['<6h']}`} size="small" color="success" variant="outlined" />
                            <Chip label={`6-12h: ${ageBuckets['6-12h']}`} size="small" color="info" variant="outlined" />
                            <Chip label={`12-24h: ${ageBuckets['12-24h']}`} size="small" color="warning" variant="outlined" />
                            <Chip label={`>24h: ${ageBuckets['>24h']}`} size="small" color="error" variant="outlined" />
                            {ageBuckets['unknown'] > 0 && (
                              <Chip label={`unknown: ${ageBuckets['unknown']}`} size="small" color="default" variant="outlined" />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                );
              })()}
            </Grid>

            {/* Pool Details Table */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
              <Typography variant="h6">Pool Details</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {/* Type filters */}
                {['all', 'category', 'sector', 'stock'].map((type) => (
                  <Chip
                    key={`type-${type}`}
                    label={type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
                    size="small"
                    variant={poolFilter === type ? 'filled' : 'outlined'}
                    color="primary"
                    onClick={() => updatePoolFilter(type as any)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                {/* Status filters */}
                {['oldest', 'stale', 'expired', 'zero'].map((filter) => (
                  <Chip
                    key={filter}
                    label={
                      filter === 'oldest' ? '🔥 Oldest First' :
                      filter === 'stale' ? 'Stale+' :
                      filter === 'expired' ? 'Expired' :
                      'Zero Articles'
                    }
                    size="small"
                    variant={poolFilter === filter ? 'filled' : 'outlined'}
                    color={filter === 'expired' ? 'error' : filter === 'stale' ? 'warning' : 'default'}
                    onClick={() => updatePoolFilter(filter as any)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // Export filtered pools to CSV
                    const filteredPools = Object.entries(enhancedStatus.pools)
                      .filter(([key, pool]: [string, any]) => {
                        if (poolFilter === 'category') return key.startsWith('category:');
                        if (poolFilter === 'sector') return key.startsWith('sector:');
                        if (poolFilter === 'stock') return key.startsWith('stock:');
                        if (poolFilter === 'stale') return pool.freshnessStatus === 'stale' || pool.freshnessStatus === 'expired' || pool.freshnessStatus === 'not_found';
                        if (poolFilter === 'expired') return pool.freshnessStatus === 'expired' || pool.freshnessStatus === 'not_found';
                        if (poolFilter === 'zero') return (pool.counts?.articles ?? 0) === 0;
                        return true;
                      })
                      .sort(([keyA, a]: [string, any], [keyB, b]: [string, any]) => {
                        const ageA = Number.isFinite(a.ageHours) ? a.ageHours : -1;
                        const ageB = Number.isFinite(b.ageHours) ? b.ageHours : -1;
                        if (ageB !== ageA) return ageB - ageA;
                        return keyA.localeCompare(keyB);
                      })
                      .slice(0, poolFilter === 'oldest' ? 10 : undefined);

                    const csvContent = [
                      ['Type', 'Symbol', 'Articles', 'Fresh', 'Age (h)', 'Status', 'Last Updated'].join(','),
                      ...filteredPools.map(([poolKey, pool]: [string, any]) => {
                        const [type, symbol] = poolKey.split(':');
                        return [
                          type,
                          symbol,
                          pool.counts?.articles ?? 0,
                          pool.counts?.fresh ?? 0,
                          pool.ageHours?.toFixed(1) ?? '-',
                          pool.freshnessStatus || 'unknown',
                          pool.fetchedAt || '-'
                        ].join(',');
                      })
                    ].join('\n');

                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `article-pool-details-${poolFilter}-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);

                    console.log('[Resources] pool_details_csv_export', {
                      filter: poolFilter,
                      rows: filteredPools.length,
                      timestamp: new Date().toISOString(),
                    });
                  }}
                >
                  Export CSV
                </Button>
              </Box>
            </Box>
            <Card>
              <CardContent>
                <Box sx={{ overflowX: 'auto' }}>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table ref={poolTableRef} size="small" stickyHeader tabIndex={-1}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Articles</TableCell>
                        <TableCell align="right">Fresh</TableCell>
                        <TableCell align="right">Age (h)</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(enhancedStatus.pools)
                        .filter(([key, pool]: [string, any]) => {
                          if (poolFilter === 'category') return key.startsWith('category:');
                          if (poolFilter === 'sector') return key.startsWith('sector:');
                          if (poolFilter === 'stock') return key.startsWith('stock:');
                          if (poolFilter === 'stale') return pool.freshnessStatus === 'stale' || pool.freshnessStatus === 'expired' || pool.freshnessStatus === 'not_found';
                          if (poolFilter === 'expired') return pool.freshnessStatus === 'expired' || pool.freshnessStatus === 'not_found';
                          if (poolFilter === 'zero') return (pool.counts?.articles ?? 0) === 0;
                          return true;
                        })
                        .sort(([keyA, a]: [string, any], [keyB, b]: [string, any]) => {
                          // Always sort by age descending (oldest/critical first)
                          const ageA = Number.isFinite(a.ageHours) ? a.ageHours : -1;
                          const ageB = Number.isFinite(b.ageHours) ? b.ageHours : -1;
                          if (ageB !== ageA) return ageB - ageA;
                          // Tie-breaker: alphabetical by pool key
                          return keyA.localeCompare(keyB);
                        })
                        .slice(0, poolFilter === 'oldest' ? 10 : undefined)
                        .map(([poolKey, pool]: [string, any]) => {
                          const [type, symbol] = poolKey.split(':');
                          const isCritical = (pool.ageHours ?? 0) > CRITICAL_AGE_HOURS;
                          return (
                        <TableRow key={poolKey} sx={isCritical ? { bgcolor: 'error.main', '& td': { color: 'white' } } : undefined}>
                          <TableCell>
                            <Chip 
                              label={type} 
                              size="small" 
                              variant="outlined"
                              sx={isCritical ? { borderColor: 'white', color: 'white' } : undefined}
                              color={type === 'category' ? 'primary' : type === 'sector' ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{symbol}</TableCell>
                          <TableCell align="right">{pool.counts?.articles ?? 0}</TableCell>
                          <TableCell align="right">{pool.counts?.fresh ?? 0}</TableCell>
                          <TableCell align="right">{pool.ageHours?.toFixed(1) ?? '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={pool.source || (poolKey.startsWith('category:') ? 'NewsData' : '-')}
                              size="small"
                              variant="outlined"
                              color={pool.source === 'Finnhub' ? 'info' : pool.source === 'Blended' ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isCritical ? `CRITICAL ${(pool.ageHours ?? 0).toFixed(0)}h` : (pool.freshnessStatus || 'unknown')}
                              size="small"
                              color={isCritical ? 'error' : pool.freshnessStatus === 'fresh' ? 'success' : pool.freshnessStatus === 'stale' ? 'warning' : 'error'}
                              sx={isCritical ? { fontWeight: 'bold' } : undefined}
                              aria-label={isCritical ? `Critical pool, age ${(pool.ageHours ?? 0).toFixed(0)} hours` : `Pool status: ${pool.freshnessStatus || 'unknown'}`}
                            />
                          </TableCell>
                          <TableCell sx={isCritical ? { color: 'white' } : undefined}>
                            {pool.fetchedAt ? new Date(pool.fetchedAt).toLocaleString('en-US', {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                        </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Section 4: Storage Architecture (Technical Detail) */}
        {enhancedStatus && (
          <Accordion variant="outlined" sx={{ mb: 4 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <StorageIcon fontSize="small" />
                <Typography>Storage Architecture (DO + KV)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>KV Storage (Primary)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Durable primary storage with :latest/:backup pattern. Source of truth for article data.
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Pools: {enhancedStatus.summary?.totalPools || 0}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>Durable Object (Cache)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fast SQLite-backed cache for low-latency reads. Best-effort sync from KV.
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </CustomTabPanel>

      {/* Tab 3: Money Flow Pool */}
      <CustomTabPanel value={tabValue} index={2}>
        {/* Money Flow Pool Status Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Money Flow Pool Status
            {systemHealth?.services?.moneyFlowPool?.status === 'healthy' && <CheckCircleIcon sx={{ color: 'success.main' }} />}
            {systemHealth?.services?.moneyFlowPool?.status === 'degraded' && <WarningAmberIcon sx={{ color: 'warning.main' }} />}
            {systemHealth?.services?.moneyFlowPool?.status === 'critical' && <ErrorIcon sx={{ color: 'error.main' }} />}
          </Typography>

          {systemHealth?.services?.moneyFlowPool ? (
            <>
              {/* Status Alert */}
              {systemHealth.services.moneyFlowPool.issues.length > 0 && (
                <Alert
                  severity={
                    systemHealth.services.moneyFlowPool.status === 'healthy' ? 'info' :
                    systemHealth.services.moneyFlowPool.status === 'degraded' ? 'warning' :
                    'error'
                  }
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {systemHealth.services.moneyFlowPool.status === 'healthy'
                      ? 'Money Flow Pool Operating Normally'
                      : systemHealth.services.moneyFlowPool.status === 'degraded'
                      ? 'Money Flow Pool Performance Degraded'
                      : 'Money Flow Pool Critical - Immediate Attention Required'}
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    {systemHealth.services.moneyFlowPool.issues.map((issue: string, idx: number) => (
                      <li key={idx}>
                        <Typography variant="body2">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Pool Completeness */}
                <Grid size={{xs: 12, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Pool Completeness
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: { xs: '3rem', md: '3.75rem' } }}>
                            {systemHealth.services.moneyFlowPool.completeness}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={systemHealth.services.moneyFlowPool.completeness}
                          sx={{
                            mb: 1,
                            height: 10,
                            borderRadius: 1,
                            backgroundColor: 'action.disabledBackground',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: systemHealth.services.moneyFlowPool.completeness >= 80
                                ? 'success.main'
                                : systemHealth.services.moneyFlowPool.completeness >= 50
                                ? 'warning.main'
                                : 'error.main'
                            }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                          Target: 80% • {systemHealth.services.moneyFlowPool.totalSymbols} symbols tracked
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Pool Breakdown */}
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Pool Breakdown
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Fresh (&lt;12h)
                          </Typography>
                          <Chip
                            label={systemHealth.services.moneyFlowPool.fresh}
                            color="success"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Stale (&gt;12h)
                          </Typography>
                          <Chip
                            label={systemHealth.services.moneyFlowPool.stale}
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Missing
                          </Typography>
                          <Chip
                            label={systemHealth.services.moneyFlowPool.missing}
                            color="error"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        {systemHealth.services.moneyFlowPool.avgStalenessHours > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Staleness
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {systemHealth.services.moneyFlowPool.avgStalenessHours.toFixed(1)}h
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Last Warming Info */}
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Warming Status
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Last Warmed At
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {systemHealth.services.moneyFlowPool.lastWarmAt
                              ? new Date(systemHealth.services.moneyFlowPool.lastWarmAt).toLocaleString()
                              : 'Never'}
                          </Typography>
                        </Box>
                        {systemHealth.services.moneyFlowPool.lastWarmAt && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Time Since Last Warm
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {Math.round((Date.now() - new Date(systemHealth.services.moneyFlowPool.lastWarmAt).getTime()) / 1000 / 60 / 60)} hours ago
                            </Typography>
                          </Box>
                        )}
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption">
                            Money Flow Pool should be warmed every 12 hours to maintain data freshness.
                          </Typography>
                        </Alert>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Loading Money Flow Pool status...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Money Flow Health Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Money Flow Health & Diagnostics
            {moneyFlowHealth?.health?.status === 'healthy' && <CheckCircleIcon sx={{ color: 'success.main' }} />}
            {moneyFlowHealth?.health?.status === 'degraded' && <WarningAmberIcon sx={{ color: 'warning.main' }} />}
            {moneyFlowHealth?.health?.status === 'critical' && <ErrorIcon sx={{ color: 'error.main' }} />}
          </Typography>

          {moneyFlowHealth ? (
            <>
              <Grid container spacing={3}>
                {/* SLO Achievement */}
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        SLO Achievement
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Success Rate</Typography>
                          <Chip
                            label={moneyFlowHealth.health.slo.achieved.successRate ? '✓' : '✗'}
                            color={moneyFlowHealth.health.slo.achieved.successRate ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (moneyFlowHealth.stats.successRate / moneyFlowHealth.health.slo.successRateTarget) * 100)}
                          sx={{ mb: 0.5, height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {moneyFlowHealth.stats.successRate.toFixed(1)}% / {moneyFlowHealth.health.slo.successRateTarget}% target
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 2 }}>
                          <Typography variant="body2">Data Quality</Typography>
                          <Chip
                            label={moneyFlowHealth.health.slo.achieved.dataQuality ? '✓' : '✗'}
                            color={moneyFlowHealth.health.slo.achieved.dataQuality ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, (moneyFlowHealth.stats.dataQualityScore / moneyFlowHealth.health.slo.dataQualityTarget) * 100)}
                          sx={{ mb: 0.5, height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {moneyFlowHealth.stats.dataQualityScore.toFixed(1)} / {moneyFlowHealth.health.slo.dataQualityTarget} target
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Statistics */}
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Statistics
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Total Attempts</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {moneyFlowHealth.stats.totalAttempts}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Successful</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {moneyFlowHealth.stats.successful}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Failed</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            {moneyFlowHealth.stats.failed}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Reach Rate
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 'bold',
                              color: moneyFlowHealth.stats.upstreamReachRate < 50
                                ? 'error.main'
                                : moneyFlowHealth.stats.upstreamReachRate < 80
                                ? 'warning.main'
                                : 'success.main'
                            }}
                          >
                            {moneyFlowHealth.stats.upstreamReachRate.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Source Performance */}
                <Grid size={{xs: 12, sm: 12, md: 4}}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Source Performance
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {Object.entries(moneyFlowHealth.stats.bySource).map(([source, stats]: [string, any]) => (
                          <Box key={source} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 'bold', mb: 0.5 }}>
                              {source}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Attempts</Typography>
                              <Typography variant="caption">{stats.attempts}</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={stats.attempts > 0 ? (stats.success / stats.attempts) * 100 : 0}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Loading money flow health data...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Cache Warming History (D1-based) */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Cache Warming History
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Real-time warming results from Cloudflare Workers • Updated after each warming run
          </Typography>

          {/* Error Alert */}
          {warmingHistoryError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Unable to fetch warming history:</strong> {warmingHistoryError}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Check D1 database connection or try refreshing the page.
              </Typography>
            </Alert>
          )}

          {warmingHistory.length > 0 && (
            <Grid container spacing={3}>
              {warmingHistory.map((runGroup, index) => (
                <Grid size={{ xs: 12 }} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">
                            {new Date(runGroup.timestamp).toLocaleString('en-US', {
                              timeZone: 'Asia/Taipei',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Triggered by: {runGroup.triggeredBy === 'github_actions' ? 'GitHub Actions' : 'Manual'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={`${runGroup.summary.dataRefreshed} refreshed`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`${runGroup.summary.timestampOnly} timestamp-only`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                          {runGroup.summary.failed > 0 && (
                            <Chip
                              label={`${runGroup.summary.failed} failed`}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Total duration: {(runGroup.summary.totalDuration / 1000).toFixed(1)}s • Average: {Math.round(runGroup.summary.totalDuration / runGroup.summary.total)}ms per symbol
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Empty State */}
          {!warmingHistoryError && warmingHistory.length === 0 && !loading && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No warming history available yet. Run a cache warming operation to see results here.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </CustomTabPanel>
      </Box>
    </>
  );
}
